import React from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LinkButton } from '@/components/ui/buttons';
import { DynamicFiltersCard } from '@/components/ui/cards/DynamicFiltersCard';
import { useConceptosPagoList } from '@/hooks/payment';


export const ConceptosPagoPage: React.FC = () => {
  const { t: translate } = useTranslation('payment');
  const {
    year,
    month,
    monthlyCutId,
    setYear,
    setMonth,
    setMonthlyCutId,
    yearOptions,
    monthOptions,
    monthlyCutOptions,
    loadingMonthlyCuts,
    handleSearch,
    hasSearched,
    data,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
  } = useConceptosPagoList();

  return (
    <PageLayout dynamicTitle={translate('list.title')}>
      <div className="flex flex-col gap-6" data-testid="conceptos-pago-list-page">
        <DynamicFiltersCard
          columns={4}
          fields={[
            {
              id: 'year',
              type: 'selector',
              colSpan: 1,
              loading: loadingMonthlyCuts,
              component: (
                <Selector
                  label={translate('list.filters.year')}
                  placeholder={translate('list.filters.selectYear')}
                  value={year ? String(year) : ''}
                  onChange={(value) => setYear(Number(value) || undefined)}
                  options={yearOptions}
                  isLoading={loadingMonthlyCuts}
                  data-testid="conceptos-pago-year-selector"
                />
              ),
            },
            {
              id: 'month',
              type: 'selector',
              colSpan: 1,
              loading: loadingMonthlyCuts,
              component: (
                <Selector
                  label={translate('list.filters.month')}
                  placeholder={translate('list.filters.selectMonth')}
                  value={month ? String(month) : ''}
                  onChange={(value) => setMonth(Number(value) || undefined)}
                  options={monthOptions}
                  disabled={!year}
                  isLoading={loadingMonthlyCuts}
                  data-testid="conceptos-pago-month-selector"
                />
              ),
            },
            {
              id: 'description',
              type: 'selector',
              colSpan: 1,
              loading: loadingMonthlyCuts,
              component: (
                <Selector
                  key={`monthly-cut-${year}-${month}`}
                  label={translate('list.filters.description')}
                  placeholder={translate('list.filters.selectDescription')}
                  value={monthlyCutId ? String(monthlyCutId) : ''}
                  onChange={(value) => {
                    const stringValue = Array.isArray(value) ? value[0] : value;
                    setMonthlyCutId(stringValue ? Number(stringValue) : undefined);
                  }}
                  options={monthlyCutOptions}
                  disabled={!year || !month}
                  isLoading={loadingMonthlyCuts}
                  data-testid="conceptos-pago-description-selector"
                />
              ),
            },
          ]}
          actions={
            <PrimaryButton
              onClick={handleSearch}
              disabled={!monthlyCutId}
              data-testid="conceptos-pago-search-button"
            >
              {translate('list.actions.search')}
            </PrimaryButton>
          }
        />

        <div className="flex justify-end">
          <LinkButton disabled>
            {translate('list.actions.download')}
          </LinkButton>
        </div>

        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'generadoEl', direction: 'desc' }}
          emptyMessage={
            hasSearched
              ? translate('list.messages.noResults')
              : translate('list.messages.selectFilters')
          }
          data-testid="table-conceptos-pago"
        />
      </div>
    </PageLayout>
  );
};

export default ConceptosPagoPage;
